import { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { getPostBySlug, blogPosts } from '@/data/blogPosts';
import { Calendar, Clock, ArrowLeft, User } from 'lucide-react';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | Amjed Tours Blog`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', post.excerpt);
    }
    return () => { document.title = 'Amjed Tours | Car Rental in Pakistan - Islamabad, Lahore, Karachi'; };
  }, [slug]);

  if (!post) return <Navigate to="/blog" />;

  // Convert simple markdown to HTML
  const renderContent = (text) => {
    return text.split('\n\n').map((paragraph, i) => {
      if (paragraph.startsWith('## ')) {
        return <h2 key={i} className="text-2xl font-bold mt-8 mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>{paragraph.slice(3)}</h2>;
      }
      if (paragraph.startsWith('# ')) {
        return <h1 key={i} className="text-3xl font-bold mt-8 mb-4">{paragraph.slice(2)}</h1>;
      }
      if (paragraph.match(/^\d+\. /) || paragraph.startsWith('- ')) {
        const items = paragraph.split('\n').filter(l => l.trim());
        return (
          <ul key={i} className="my-4 space-y-2 list-disc list-inside">
            {items.map((item, j) => <li key={j}>{item.replace(/^(\d+\. |- )/, '')}</li>)}
          </ul>
        );
      }
      if (paragraph.startsWith('|')) {
        const lines = paragraph.split('\n');
        const headers = lines[0].split('|').filter(c => c.trim()).map(c => c.trim());
        const rows = lines.slice(2).map(row => row.split('|').filter(c => c.trim()).map(c => c.trim()));
        return (
          <table key={i} className="my-6 w-full border border-border">
            <thead className="bg-muted">
              <tr>{headers.map((h, j) => <th key={j} className="p-3 text-left border border-border">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row, k) => (
                <tr key={k}>{row.map((cell, l) => <td key={l} className="p-3 border border-border">{cell}</td>)}</tr>
              ))}
            </tbody>
          </table>
        );
      }
      // Bold markdown
      const parts = paragraph.split(/\*\*(.*?)\*\*/g);
      return (
        <p key={i} className="my-4 leading-relaxed">
          {parts.map((p, j) => j % 2 === 1 ? <strong key={j}>{p}</strong> : p)}
        </p>
      );
    });
  };

  const relatedPosts = blogPosts.filter(p => p.slug !== slug).slice(0, 2);

  return (
    <div>
      <Navbar />

      <article className="max-w-4xl mx-auto px-6 py-12">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>

        <div className="mb-8">
          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {post.readTime}</span>
            <span className="flex items-center gap-1"><User className="w-4 h-4" /> {post.author}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="blog-post-title">{post.title}</h1>
          <p className="text-lg text-muted-foreground">{post.excerpt}</p>
        </div>

        <div className="aspect-video rounded-lg overflow-hidden mb-8">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        </div>

        <div className="prose prose-lg max-w-none" data-testid="blog-post-content">
          {renderContent(post.content)}
        </div>

        {/* CTA */}
        <div className="glass-card p-8 rounded-lg my-12 text-center">
          <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Cabinet Grotesk' }}>Ready to Book?</h3>
          <p className="text-muted-foreground mb-6">Browse our fleet of premium rental cars</p>
          <Link to="/cars">
            <Button className="btn-primary">Browse Cars</Button>
          </Link>
        </div>

        {/* Related */}
        {relatedPosts.length > 0 && (
          <div className="mt-12 pt-12 border-t border-border">
            <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Cabinet Grotesk' }}>You May Also Like</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {relatedPosts.map((rp) => (
                <Link key={rp.slug} to={`/blog/${rp.slug}`} className="car-card rounded-lg overflow-hidden bg-white group">
                  <div className="aspect-video overflow-hidden">
                    <img src={rp.image} alt={rp.title} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-4">
                    <h4 className="font-bold group-hover:text-accent transition-colors">{rp.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
